<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AuthLayout from './AuthLayout.vue'
import ButtonSeed from '@/design/primitives/ButtonSeed.vue'
import { api } from '@/api/client.js'
import { EP }  from '@/api/endpoints.js'

const route  = useRoute()
const router = useRouter()

/* 'checking' | 'verified' | 'error' | 'waiting' | 'resent' | 'resend-error' */
const state     = ref('checking')
const stateMsg  = ref('')
const resending = ref(false)

let pollTimer = null

function stopPolling() {
  if (pollTimer !== null) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

async function pollVerificationStatus() {
  try {
    const res = await api.get(EP.authVerificationStatus)
    if (res.data.verified) {
      stopPolling()
      router.push('/profile')
    }
  } catch {
    /* Token invalid / expired — stop polling, stay on page */
    stopPolling()
  }
}

function startPolling() {
  stopPolling()
  pollTimer = setInterval(pollVerificationStatus, 5000)
}

async function verifyWithToken(token) {
  state.value = 'checking'
  try {
    /* Server uses GET /api/auth/verify-email?token=... */
    await api.get(EP.authVerifyEmail, { params: { token } })
    state.value = 'verified'
    setTimeout(() => router.push('/profile'), 2000)
  } catch (err) {
    state.value = 'error'
    stateMsg.value = err.message || 'Verification failed.'
  }
}

async function resend() {
  resending.value = true
  stateMsg.value  = ''
  try {
    await api.post(EP.authSendVerify)
    state.value = 'resent'
    startPolling()
  } catch (err) {
    state.value    = 'resend-error'
    stateMsg.value = err.message || 'Could not send verification email.'
  } finally {
    resending.value = false
  }
}

onMounted(async () => {
  const token = route.query.token
  if (token) {
    await verifyWithToken(token)
  } else {
    /* No token — show "check inbox" and start polling */
    state.value = 'waiting'
    startPolling()
  }
})

onUnmounted(() => { stopPolling() })
</script>

<template>
  <AuthLayout kicker="EVOLUXU · CONFIRM" stamp="Patience. The seed is settling.">

    <!-- Checking token in progress -->
    <template v-if="state === 'checking'">
      <p class="plate__muted u-display u-allcaps">VERIFYING…</p>
    </template>

    <!-- Verified via token link -->
    <template v-else-if="state === 'verified'">
      <div class="seal seal--status u-rise-in">
        <p class="seal__label u-display u-allcaps u-gold">EMAIL VERIFIED</p>
        <p class="seal__body">Redirecting you now…</p>
      </div>
    </template>

    <!-- Token verify failed -->
    <template v-else-if="state === 'error'">
      <div class="seal seal--status u-rise-in">
        <p class="seal__label u-display u-allcaps">VERIFICATION FAILED</p>
        <p class="seal__body">{{ stateMsg || 'The link may be expired or already used.' }}</p>
      </div>
      <ButtonSeed
        variant="ghost"
        size="md"
        :disabled="resending"
        class="seal__resend"
        @click="resend"
      >
        {{ resending ? 'SENDING…' : 'RESEND LINK' }}
      </ButtonSeed>
    </template>

    <!-- Waiting — no token in URL, polling -->
    <template v-else-if="state === 'waiting'">
      <div class="seal seal--status u-rise-in">
        <p class="seal__label u-display u-allcaps">CHECK YOUR INBOX</p>
        <p class="seal__body">
          We sent a verification link to your email. Click it to continue.
          This page updates automatically once verified.
        </p>
      </div>
      <ButtonSeed
        variant="ghost"
        size="md"
        :disabled="resending"
        class="seal__resend"
        @click="resend"
      >
        {{ resending ? 'SENDING…' : 'RESEND LINK' }}
      </ButtonSeed>
    </template>

    <!-- Resent successfully -->
    <template v-else-if="state === 'resent'">
      <div class="seal seal--status u-rise-in">
        <p class="seal__label u-display u-allcaps u-gold">LINK SENT</p>
        <p class="seal__body">
          A new verification link is on its way. Check your inbox.
          This page updates automatically.
        </p>
      </div>
      <ButtonSeed
        variant="ghost"
        size="md"
        :disabled="resending"
        class="seal__resend"
        @click="resend"
      >
        {{ resending ? 'SENDING…' : 'RESEND AGAIN' }}
      </ButtonSeed>
    </template>

    <!-- Resend failed -->
    <template v-else-if="state === 'resend-error'">
      <div class="seal seal--status u-rise-in">
        <p class="seal__label u-display u-allcaps">COULD NOT SEND</p>
        <p class="seal__body">{{ stateMsg || 'Something went wrong. Please try again.' }}</p>
      </div>
      <ButtonSeed
        variant="ghost"
        size="md"
        :disabled="resending"
        class="seal__resend"
        @click="resend"
      >
        {{ resending ? 'SENDING…' : 'TRY AGAIN' }}
      </ButtonSeed>
    </template>

  </AuthLayout>
</template>

<style scoped>
.plate__muted {
  font-size: var(--fs-12);
  color: var(--ink-mute);
  letter-spacing: 0.14em;
  margin: 0;
  text-align: center;
  padding: var(--sp-8) 0;
}

/* ── Status blocks ── */
.seal--status {
  text-align: center;
  padding: var(--sp-6) 0;
}

.seal__label {
  margin: 0 0 var(--sp-3);
  font-size: var(--fs-21);
  letter-spacing: 0.08em;
  color: var(--ink-soft);
}

.seal__body {
  margin: 0;
  font-size: var(--fs-16);
  color: var(--ink-soft);
  line-height: var(--lh-body);
}

.seal__resend {
  width: 100%;
  margin-top: var(--sp-6);
  font-family: var(--font-display);
  letter-spacing: 0.10em;
}
</style>
